import axios from 'axios';
import { guardarTokenParaSW } from '../utils/pushTokenCache';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
    headers: { 'Content-Type': 'application/json' }
});

// Admin cargando un pedido a nombre de una empresa (29-oct-2026): mientras está abierto el "Nuevo pedido"
// de la empresa, las llamadas a /empresa/... van con X-Como-Empresa (el backend lo acepta solo para esas rutas).
let comoEmpresaId = null;
export function setComoEmpresa(clienteId) { comoEmpresaId = clienteId ? String(clienteId) : null; }

// Adjunta el token JWT y limpia Content-Type para FormData
api.interceptors.request.use(config => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (comoEmpresaId && String(config.url || '').startsWith('/empresa/')) {
        config.headers['X-Como-Empresa'] = comoEmpresaId;
    }
    // Dejar que el navegador establezca Content-Type con boundary correcto
    if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
    }
    // Testeo integral A5 (7-oct-2026): cada cambio lleva una clave única que se repite
    // en los reintentos. Si el primer intento sí llegó al servidor (señal mala), el
    // backend devuelve la misma respuesta en vez de guardar dos veces (IdempotenciaFilter).
    const metodo = (config.method || 'get').toLowerCase();
    if (metodo !== 'get' && metodo !== 'head' && !config.headers['X-Idem-Key']) {
        config.headers['X-Idem-Key'] = config._idemKey || nuevaClaveIdem();
    }
    return config;
});

export function nuevaClaveIdem() {
    try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

// Spring Boot tarda ~20-40s en arrancar, entonces 8 reintentos × 5s = 40s de ventana
const MAX_REINTENTOS = 8;
const DELAY_MS       = 5000;

// Bug real (9-sep): cuando el JWT vencía, CADA pedido que fallaba en simultáneo
// (badges, notificaciones, radar, mi-espacio...) disparaba su propio POST a
// /auth/refresh por separado -- no rompía nada (el backend tolera refresh
// concurrente bien, ver RefreshTokenService.validarYRenovar), pero eran N
// llamadas redundantes al mismo tiempo y N líneas de 401 en la consola de
// Chrome (que loguea el intento fallido original aunque el reintento después
// sea exitoso). Ahora todos los pedidos que fallan a la vez esperan el MISMO
// refresh en curso en vez de disparar uno cada uno.
let refreshEnCurso = null;
function refrescarToken(refreshToken) {
    if (!refreshEnCurso) {
        refreshEnCurso = axios.post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken })
            .finally(() => { refreshEnCurso = null; });
    }
    return refreshEnCurso;
}

api.interceptors.response.use(
    res => res,
    async error => {
        const config = error.config;

        // Si recibimos 401 en cualquier pedido normal (no en login ni en el
        // propio refresh, para no entrar en loop), el access token venció o
        // es inválido. Antes de forzar logout, se intenta UNA vez canjear el
        // refresh token guardado (ver AuthContext/RefreshTokenService) por
        // uno nuevo — así una sesión activa no se corta solo porque pasaron
        // 24hs, y de paso se mantiene fresco el token que el service worker
        // usa para las notificaciones push (ver pushTokenCache.js).
        const esRefreshOLogin = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh');
        if (error.response?.status === 401 && !esRefreshOLogin && !config._reintentoRefresh) {
            config._reintentoRefresh = true;
            const refreshToken = localStorage.getItem('auth_refresh_token');
            if (refreshToken) {
                try {
                    const { data } = await refrescarToken(refreshToken);
                    localStorage.setItem('auth_token', data.accessToken);
                    guardarTokenParaSW(data.accessToken);
                    config.headers['Authorization'] = `Bearer ${data.accessToken}`;
                    return api(config);
                } catch (errRefresh) {
                    // Sin señal / backend reiniciando (2-oct-2026): el refresh ni
                    // llegó al servidor. NO cerrar la sesión — el técnico en la calle
                    // perdía todo lo que tenía abierto. Se rechaza este pedido y el
                    // próximo vuelve a intentar el refresh cuando haya conexión.
                    if (!errRefresh?.response) return Promise.reject(error);
                    // Testeo integral A8 (7-oct-2026): durante actualizar-backend.bat el
                    // servidor responde 502/503 un rato; eso tampoco es "sesión vencida".
                    // Solo se cierra la sesión si el servidor rechazó el refresh (400/401/403).
                    if (![400, 401, 403].includes(errRefresh.response.status)) return Promise.reject(error);
                    // El refresh token también venció o fue revocado (ej.
                    // usuario desactivado) — ahí sí, logout real.
                }
            }
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_usuario');
            localStorage.removeItem('auth_refresh_token');
            guardarTokenParaSW(null);
            window.location.reload();
            return Promise.reject(error);
        }

        // Solo reintentar si no hubo respuesta del servidor (red caída / servidor no listo)
        // _sinReintento: pedidos que van por la cola sin señal (utils/pendientesOffline.js)
        // — no hacer esperar 40s al técnico, se guardan y se mandan después.
        if (!error.response && config && !config._sinReintento) {
            config._reintento = (config._reintento || 0) + 1;
            if (config._reintento <= MAX_REINTENTOS) {
                await new Promise(r => setTimeout(r, DELAY_MS));
                return api(config);
            }
        }
        return Promise.reject(error);
    }
);
// ── Helpers de paginación ────────────────────────────────────────────────────
// Para listas de selección en formularios (dropdowns, selects)
const PAGE_ALL = '?page=0&size=500';

// Construye query string a partir de un objeto de parámetros
function buildQuery(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    return q.toString() ? `?${q.toString()}` : '';
}

// ── Clientes ─────────────────────────────────────────────────────────────────
export const getClientes  = () => api.get(`/clientes${PAGE_ALL}`);
export const crearCliente = (data) => api.post('/clientes', data);
export const editarCliente = (id, data) => api.put(`/clientes/${id}`, data);
export const eliminarCliente = (id) => api.delete(`/clientes/${id}`);

// ── Sedes ─────────────────────────────────────────────────────────────────────
export const getSedes   = () => api.get(`/sedes${PAGE_ALL}`);
export const crearSede  = (data) => api.post('/sedes', data);
export const editarSede = (id, data) => api.put(`/sedes/${id}`, data);
export const eliminarSede = (id) => api.delete(`/sedes/${id}`);
export const eliminarSedeDefinitivo = (id) => api.delete(`/sedes/${id}/definitivo`);

// ── Equipos ───────────────────────────────────────────────────────────────────
export const getEquipos   = () => api.get(`/equipos${PAGE_ALL}`);
export const crearEquipo  = (data) => api.post('/equipos', data);
export const editarEquipo = (id, data) => api.put(`/equipos/${id}`, data);
export const archivarEquipo = (id) => api.delete(`/equipos/${id}`);
export const restaurarEquipo = (id) => api.patch(`/equipos/${id}/restaurar`);
export const eliminarEquipoDefinitivo = (id) => api.delete(`/equipos/${id}/definitivo`);

// ── Repuestos ─────────────────────────────────────────────────────────────────
// Sin parámetros → para selectores en formularios (carga todos)
// Con parámetros { busqueda, page, size } → para el gestor paginado
export const getRepuestos = (params = {}) =>
    Object.keys(params).length === 0
        ? api.get(`/repuestos${PAGE_ALL}`)
        : api.get(`/repuestos${buildQuery({ page: 0, size: 20, ...params })}`);
export const crearRepuesto  = (data) => api.post('/repuestos', data);
export const editarRepuesto = (id, data) => api.put(`/repuestos/${id}`, data);
export const eliminarRepuesto = (id) => api.delete(`/repuestos/${id}`);

// ── Servicios ─────────────────────────────────────────────────────────────────
// Sin parámetros → carga todo (compatibilidad con formularios/historial)
// Con parámetros → paginación real con filtros (tipo, estado, busqueda, desde, hasta, page, size, sort)
export const getServicios = (params = {}) =>
    Object.keys(params).length === 0
        ? api.get(`/servicios?page=0&size=500&sort=fechaServicio,desc`)
        : api.get(`/servicios${buildQuery({ page: 0, size: 20, sort: 'fechaServicio,desc', ...params })}`);

// Stats resumen del panel — separado de la lista paginada
export const getServiciosResumen = (tipo) =>
    api.get(`/servicios/resumen${tipo ? `?tipo=${tipo}` : ''}`);

export const crearServicio  = (data) => api.post('/servicios', data);
export const editarServicio = (id, data) => api.put(`/servicios/${id}`, data);
export const eliminarServicio = (id) => api.delete(`/servicios/${id}`);

// ── Ventas ────────────────────────────────────────────────────────────────────
export const getVentas  = () => api.get(`/ventas?page=0&size=500`);
export const crearVenta = (data) => api.post('/ventas', data);

// ── Gastos ────────────────────────────────────────────────────────────────────
export const getGastos  = () => api.get(`/gastos?page=0&size=500`);
export const crearGasto = (data) => api.post('/gastos', data);
export const eliminarGasto = (id) => api.delete(`/gastos/${id}`);

// ── Mi Espacio ────────────────────────────────────────────────────────────────
export const getMiEspacio    = () => api.get('/mi-espacio');
export const guardarMiEspacio = (espacioJson) => api.put('/mi-espacio', { espacioJson });
// Checklists de todos los tecnicos (solo admin) -- Lucas, 8-sep-2026, para poder ver
// el bloc de notas de cada uno sin tener que pedirselo. Ver MiEspacioController.java.
export const getMiEspacioTecnicos = () => api.get('/mi-espacio/admin/tecnicos');

// ── Admin: Usuarios ───────────────────────────────────────────────────────────
export const getUsuarios        = () => api.get('/admin/usuarios');
export const crearUsuario       = (data) => api.post('/admin/usuarios', data);
export const editarUsuario      = (id, data) => api.put(`/admin/usuarios/${id}`, data);
export const cambiarPassword    = (id, data) => api.put(`/admin/usuarios/${id}/password`, data);
export const eliminarUsuario    = (id) => api.delete(`/admin/usuarios/${id}`);

export default api;